import { useState, useEffect } from 'react'
import { API_BASE_URL } from '../config/api'

interface AboutSection {
  id?: number
  section: 'about' | 'vision' | 'mission'
  intro_content?: string
  hospital_journey_content?: string
  future_vision_content?: string
  founder_name?: string
  founder_message?: string
  title?: string
  content?: string
  is_active?: number
}

const AboutUs = () => {
  const [activeTab, setActiveTab] = useState<'about' | 'vision' | 'mission'>('about')
  const [sections, setSections] = useState<AboutSection[]>([])
  const [loading, setLoading] = useState(true)


  const tabs = [
    { id: 'about' as const, label: 'About Us' },
    { id: 'vision' as const, label: 'Vision' },
    { id: 'mission' as const, label: 'Mission' },
  ]

  // Fetch about us sections
  const fetchSections = async () => {
    setLoading(true)
    try {
      const response = await fetch(`${API_BASE_URL}/about_us_list.php`)
      const result = await response.json()
      
      if (result.status && result.data) {
        // Handle both array and object response formats
        if (Array.isArray(result.data)) {
          setSections(result.data.filter((s: AboutSection) => (s.is_active ?? 1) === 1))
        } else {
          // Convert object to array
          const sectionsArray = Object.values(result.data) as AboutSection[]
          setSections(sectionsArray.filter((s: AboutSection) => (s.is_active ?? 1) === 1))
        }
      }
    } catch (err) {
      console.error('Error fetching about us sections:', err)
      // Keep default/empty state on error
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSections()
  }, [])

  // Get section by type
  const getSectionByType = (type: 'about' | 'vision' | 'mission') => {
    return sections.find(s => s.section === type)
  }

  const aboutSection = getSectionByType('about')
  const visionSection = getSectionByType('vision')
  const missionSection = getSectionByType('mission')

  // Default content (fallback)
  const defaultAboutContent = {
    intro_content: 'LMT SEVA named in loving memory of my father, Lalji Tiwari—was born out of personal loss and a deep desire to help others. In 2021, during the devastating COVID-19 pandemic, I lost my father. Like millions of families, we experienced firsthand the pain, helplessness, and emotional toll that came with those uncertain times. From that loss came a purpose.',
    hospital_journey_content: 'We established LMT SEVA Hospital in Gorakhpur, India, as a charity hospital offering basic life-support and essential medical care at minimal cost. For nearly two years, the hospital served patients who had nowhere else to go. For many, it was not just a hospital—it was hope.',
    future_vision_content: 'Our goal now is to relocate the hospital to a city location, where doctors can commute more easily and patients can access care without barriers. To make this dream a reality, we need the strength of a community that believes healthcare is a right, not a privilege.',
    founder_name: 'Shashi Tiwari',
    founder_message: 'When I first started LMT SEVA, I didn\'t know how I would sustain it. All I had was a belief that doing the right thing would somehow find its way forward.'
  }

  const defaultVision = {
    title: 'Our Vision',
    content: 'To reopen LMT SEVA Hospital in India and build a world where compassion leads healthcare—where every person, regardless of financial means, is treated with dignity, care, and hope.'
  }

  const defaultMission = {
    title: 'Our Mission',
    content: 'Born from personal loss and driven by service, our mission is to stand with those who have nowhere else to turn. Through community support, charitable initiatives, and purpose-driven events, we strive to reopen and sustain LMT SEVA Hospital, providing affordable—and ultimately free—healthcare to underserved communities.'
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Hero Section */}
      {/* <section className="bg-gradient-to-r from-gray-800 to-gray-900 py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              About LMT SEVA
            </h1>
            <p className="text-xl text-gray-300">
              Turning personal loss into compassionate, affordable care
            </p>
          </div>
        </div>
      </section> */}

      {/* Tab Navigation */}
      <section className="bg-gray-800 border-b border-gray-700 sticky top-20 z-40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-2 md:gap-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 text-lg font-semibold transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'text-primary-light border-b-2 border-primary-light'
                    : 'text-gray-400 hover:text-primary-light'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* About Us Tab */}
            {activeTab === 'about' && (
              <div className="space-y-8 animate-fadeIn">
                {loading ? (
                  <div className="text-center py-16">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-light mx-auto mb-4"></div>
                    <p className="text-gray-400 text-lg">Loading content...</p>
                  </div>
                ) : (
                  <>
                    {/* Introduction */}
                    {aboutSection?.intro_content && (
                      <div className="bg-gray-800 rounded-lg p-8 md:p-12">
                        <div 
                          className="text-gray-300 text-lg md:text-xl leading-relaxed space-y-4"
                          dangerouslySetInnerHTML={{ __html: aboutSection.intro_content }}
                        />
                      </div>
                    )}

                    {/* Hospital Journey */}
                    {aboutSection?.hospital_journey_content && (
                      <div className="bg-gray-800 rounded-lg p-8 md:p-12">
                        <div 
                          className="text-gray-300 text-lg md:text-xl leading-relaxed space-y-4"
                          dangerouslySetInnerHTML={{ __html: aboutSection.hospital_journey_content }}
                        />
                      </div>
                    )}

                    {/* Future Vision */}
                    {aboutSection?.future_vision_content && (
                      <div className="bg-gray-800 rounded-lg p-8 md:p-12">
                        <div 
                          className="text-gray-300 text-lg md:text-xl leading-relaxed space-y-4"
                          dangerouslySetInnerHTML={{ __html: aboutSection.future_vision_content }}
                        />
                      </div>
                    )}

                    {/* Founder Message */}
                    {aboutSection?.founder_message && (
                      <div className="bg-gray-800 rounded-lg p-8 md:p-12">
                        <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                          Message from the Founder
                        </h3>
                        {aboutSection.founder_name && (
                          <p className="text-primary-light font-semibold mb-4">
                            {aboutSection.founder_name}
                          </p>
                        )}
                        <div 
                          className="text-gray-300 text-lg md:text-xl leading-relaxed space-y-4"
                          dangerouslySetInnerHTML={{ __html: aboutSection.founder_message }}
                        />
                      </div>
                    )}

                    {/* Fallback to default if no data */}
                    {!aboutSection && (
                      <>
                        <div className="bg-gray-800 rounded-lg p-8 md:p-12">
                          <div className="text-gray-300 text-lg md:text-xl leading-relaxed space-y-4">
                            <p>
                              <strong className="text-white">LMT SEVA</strong> {defaultAboutContent.intro_content}
                            </p>
                          </div>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-8 md:p-12">
                          <div className="text-gray-300 text-lg md:text-xl leading-relaxed space-y-4">
                            <p>{defaultAboutContent.hospital_journey_content}</p>
                          </div>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-8 md:p-12">
                          <div className="text-gray-300 text-lg md:text-xl leading-relaxed space-y-4">
                            <p>{defaultAboutContent.future_vision_content}</p>
                          </div>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-8 md:p-12">
                          <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                            Message from the Founder
                          </h3>
                          <p className="text-primary-light font-semibold mb-4">
                            {defaultAboutContent.founder_name}
                          </p>
                          <div className="text-gray-300 text-lg md:text-xl leading-relaxed space-y-4">
                            <p>{defaultAboutContent.founder_message}</p>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}


            {/* Vision Tab */}
            {activeTab === 'vision' && (
              <div className="space-y-8 animate-fadeIn">
                {loading ? (
                  <div className="text-center py-16">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-light mx-auto mb-4"></div>
                    <p className="text-gray-400 text-lg">Loading content...</p>
                  </div>
                ) : (
                  <div className="bg-gray-800 rounded-lg p-8 md:p-12">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                      {visionSection?.title || defaultVision.title}
                    </h2>
                    <div className="space-y-6 text-gray-300 text-lg md:text-xl leading-relaxed">
                      {visionSection?.content ? (
                        <div dangerouslySetInnerHTML={{ __html: visionSection.content }} />
                      ) : (
                        <p>{defaultVision.content}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Mission Tab */}
            {activeTab === 'mission' && (
              <div className="space-y-8 animate-fadeIn">
                {loading ? (
                  <div className="text-center py-16">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-light mx-auto mb-4"></div>
                    <p className="text-gray-400 text-lg">Loading content...</p>
                  </div>
                ) : (
                  <div className="bg-gray-800 rounded-lg p-8 md:p-12">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                      {missionSection?.title || defaultMission.title}
                    </h2>
                    <div className="space-y-6 text-gray-300 text-lg md:text-xl leading-relaxed">
                      {missionSection?.content ? (
                        <div dangerouslySetInnerHTML={{ __html: missionSection.content }} />
                      ) : (
                        <p>{defaultMission.content}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}

export default AboutUs

